/**
 * Suggest the order a delivery run visits its stops in.
 *
 * Straight-line distance, not road distance: good enough to stop a driver
 * crossing the same district twice, and it needs no routing service. It is a
 * suggestion the planner looks at on a map before applying, never a route.
 *
 * Nearest neighbour from the despatch farm, then 2-opt until no swap of two
 * legs shortens the run. The run is open — it ends at the last stop — because
 * what is being chosen is the order of the deliveries; the drive home is the
 * driver's business. Stops with no coordinates keep their current order,
 * after the placed ones.
 */

export interface Point { lat: number; lng: number }
export interface Stop<T = number> { id: T; lat: number | null; lng: number | null }

const R_KM = 6371;
const rad = (d: number) => (d * Math.PI) / 180;

/** Great-circle distance in kilometres. */
export function distanceKm(a: Point, b: Point): number {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Length of an open run from `start` through `points` in order. */
export function runLengthKm(start: Point | null, points: Point[]): number {
  let total = 0;
  let prev = start ?? points[0];
  for (const p of points) {
    if (prev) total += distanceKm(prev, p);
    prev = p;
  }
  return total;
}

const placed = <T>(s: Stop<T>): s is Stop<T> & Point => s.lat != null && s.lng != null;

/**
 * @returns the stop ids in suggested order: placed stops first, by the
 *   heuristic, then unplaced stops in the order given.
 */
export function suggestStopOrder<T>(start: Point | null, stops: Stop<T>[]): T[] {
  const withPos = stops.filter(placed);
  const without = stops.filter((s) => !placed(s));
  if (withPos.length <= 1) return [...withPos, ...without].map((s) => s.id);

  // Nearest neighbour.
  const left = [...withPos];
  const route: (Stop<T> & Point)[] = [];
  let at: Point = start ?? left[0];
  while (left.length) {
    let best = 0;
    for (let i = 1; i < left.length; i++) {
      if (distanceKm(at, left[i]) < distanceKm(at, left[best])) best = i;
    }
    at = left[best];
    route.push(left.splice(best, 1)[0]);
  }

  // 2-opt: reverse any stretch that shortens the run, until none does.
  const d = (p: Point | null, q: Point) => (p ? distanceKm(p, q) : 0);
  let improved = true;
  let guard = 0;
  while (improved && guard++ < 200) {
    improved = false;
    for (let i = 0; i < route.length - 1; i++) {
      for (let k = i + 1; k < route.length; k++) {
        const before = i === 0 ? start : route[i - 1];
        const after = k + 1 < route.length ? route[k + 1] : null;
        // Old legs: before→route[i] and route[k]→after. New: before→route[k], route[i]→after.
        const oldLen = d(before, route[i]) + (after ? distanceKm(route[k], after) : 0);
        const newLen = d(before, route[k]) + (after ? distanceKm(route[i], after) : 0);
        if (newLen + 1e-9 < oldLen) {
          route.splice(i, k - i + 1, ...route.slice(i, k + 1).reverse());
          improved = true;
        }
      }
    }
  }
  return [...route, ...without].map((s) => s.id);
}
